import type { ComponentParameter } from "../types";
import type {
  ComponentResolution,
  ComponentResolutionMap,
} from "./buildImportRows";

/** One selectable component instance for an ambiguous name. */
export interface ComponentInstanceOption {
  /** The disambiguating id sent on the wire (D4). */
  companyComponentId: string;
  /** The pay element the instance belongs to — used as a human-readable label. */
  elementQualifiedName: string;
}

/** Ambiguity view of a single component name. */
export interface ComponentInstances {
  /** True when discovery reported the name as ambiguous. */
  isAmbiguous: boolean;
  /** Distinct selectable instances (deduplicated by companyComponentId). */
  options: ComponentInstanceOption[];
}

/**
 * Derive, per component name, whether it is ambiguous and which distinct
 * instances the PS user can pick from.
 *
 * Discovery keys parameters by component name and stamps every parameter with
 * its instance's envelope (`companyComponentId` / `isAmbiguous`). An ambiguous
 * name therefore carries parameters from several instances, so the distinct
 * `companyComponentId` values (in first-seen order) are the choices.
 */
export function deriveComponentInstances(
  paramsByComponent: Record<string, ComponentParameter[]>,
): Record<string, ComponentInstances> {
  const result: Record<string, ComponentInstances> = {};
  for (const [name, params] of Object.entries(paramsByComponent)) {
    const isAmbiguous = params.some((p) => p.isAmbiguous === true);
    const options: ComponentInstanceOption[] = [];
    const seen = new Set<string>();
    for (const p of params) {
      const id = p.companyComponentId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      options.push({
        companyComponentId: id,
        elementQualifiedName: p.elementQualifiedName,
      });
    }
    result[name] = { isAmbiguous, options };
  }
  return result;
}

/**
 * Build the {@link ComponentResolutionMap} handed to `buildImportRows` from the
 * discovery response plus the user's per-name instance selections. Only an
 * ambiguous name carries a chosen `companyComponentId`; a non-ambiguous name is
 * resolved by name alone server-side (D4 — never auto-pick an instance).
 */
export function buildComponentResolutionMap(
  paramsByComponent: Record<string, ComponentParameter[]>,
  selections: Record<string, string>,
): ComponentResolutionMap {
  const instances = deriveComponentInstances(paramsByComponent);
  const map: ComponentResolutionMap = {};
  for (const [name, { isAmbiguous }] of Object.entries(instances)) {
    const selected = selections[name];
    const resolution: ComponentResolution = { isAmbiguous };
    if (isAmbiguous && selected) {
      resolution.companyComponentId = selected;
    }
    map[name] = resolution;
  }
  return map;
}
